import { ReactNode, useCallback, useContext } from 'react'
import _ from 'lodash'
import styled from '@emotion/styled'
import { faCheck, faPlus, faQuestion, faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { AuditDecision, diagnosisReviewItemPendingClinicalInfoQuery, DiagnosisReviewItem, diagnosisReviewItemIsCodable, diagnosisReviewItemIsPendingAuditComment, diagnosisReviewItemMissingClinicalInfo, diagnosisReviewItemRequiresAuditDecision, getStatusDisplayValue, IndexedModel, PrescriptionReviewConcept, diagnosisReviewItemCanTrash } from '@fusion-advantage/formlib'
import { L2TabularListActions, L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import { FusionColor } from '../../../theme/FusionStyle'
import L6FormContext from '../l6/L6FormContext'
import { L1DiagnosisReviewAuditCommentButton, L1DiagnosisReviewRowEvidence, L1DiagnosisReviewRowSource } from '../l1/L1DiagnosisReview'
import { V2L1BooleanButton } from '../../uiv2/Button/V2L1BooleanButton'
import { L1AcceptedCodeGel, L1CodeGel } from '../l1/L1CodeGel'
import L1Tip from '../l1/L1Tip'
import { useFormSelector } from '../../../redux/formStoreHooks'
import { selectExport } from '../../../redux/selectForm'
import { L1PlainIconButton, L1ReasonableButton } from '../l1/L1Button'

const DiagnosisAuditReviewRowRoot = styled.div(({theme}) => ({
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	border: `1px solid ${theme.colors.lines}`,
	fontSize: 16,
	'> *': {
		padding: theme.spacing(1.5, 2),
	},
}))

const DiagnosisWrapper = styled.div(({theme}) => ({
	alignSelf: 'center',
	fontSize: '16px',
	fontWeight: 600,
	// prefer not to break in the middle of a word,
	// but do it to avoid breaking the grid contraints
	overflowWrap: 'break-word',
	minWidth: 0,
}))

const IcdWrapper = styled.div({
	alignSelf: 'center',
})

const ComorbidWrapper = styled.div({
	alignSelf: 'center',
	fontSize: '16px',
	fontWeight: 400,
	// prefer not to break in the middle of a word,
	// but do it to avoid breaking the grid contraints
	overflowWrap: 'break-word',
	minWidth: 0,
})

const ActionsWrapper = styled.div(({theme}) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: theme.colors[FusionColor.Third],
	padding: '0 !important',
	'> button': {
		padding: theme.spacing(2, 1.5),
	},
}))

interface DiagnosisStatusWrapperProps {
	color?: FusionColor
}

const DiagnosisStatusWrapper = styled.div<DiagnosisStatusWrapperProps>(({theme, color}) => ({
	display: 'flex',
	flexDirection: 'row',
	fontSize: '16px',
	fontWeight: color ? 600 : 'normal',
	color: color ? theme.colors[color] : 'inherit',
	justifyContent: 'flex-start',
	alignItems: 'center',
}))

const AuditDecisionWrapper = styled.div(({theme}) => ({
	display: 'flex',
	columnGap: '.5rem',
	alignItems: 'center',
	justifyItems: 'center',
	backgroundColor: theme.colors[FusionColor.Second],
}))

interface DiagnosisAuditReviewRowProps {
	model: DiagnosisReviewItem
	onChange: (updatedModel: DiagnosisReviewItem) => void
	onAuditCommentClick: () => void
	onTrashClick: () => void
}

const DiagnosisAuditReviewRow = ({model, onChange, onAuditCommentClick, onTrashClick}: DiagnosisAuditReviewRowProps) => {
	const { jumpToConcept } = useContext(L6FormContext)
	const encounterAppLastStatusDT = useFormSelector(state => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT)

	const handleDecisionChange = useCallback((auditDecision: AuditDecision|null) => {
		onChange({
			...model,
			auditDecision,
		})
	}, [model, onChange])

	const handleDxEvidenceClick = useCallback((e: React.MouseEvent) => {
		e.preventDefault()
		if(model.cid) {
			jumpToConcept(model.cid)
		}
	}, [jumpToConcept, model])

	const handleRxEvidenceClick = useCallback((e: React.MouseEvent) => {
		jumpToConcept(PrescriptionReviewConcept.cid)
	}, [jumpToConcept])

	let status: ReactNode = null
	if(diagnosisReviewItemPendingClinicalInfoQuery(model)) {
		status = (
			<DiagnosisStatusWrapper color={FusionColor.StopRed}>
				<L1Tip icon="error" tip="Missing clinicial information; please submit as query back to the clinician" />
				{getStatusDisplayValue(model.status)}
			</DiagnosisStatusWrapper>
		)
	} else if(diagnosisReviewItemMissingClinicalInfo(model)) {
		status = (
			<DiagnosisStatusWrapper color={FusionColor.OrangeMain}>
				<L1Tip icon="warning" tip="Missing clinicial information" />
				{getStatusDisplayValue(model.status)}
			</DiagnosisStatusWrapper>
		)
	} else {
		status = (
			<DiagnosisStatusWrapper>
				{getStatusDisplayValue(model.status)}
			</DiagnosisStatusWrapper>
		)
	}

	let IcdCode = diagnosisReviewItemIsCodable(model) ? L1AcceptedCodeGel : L1CodeGel

	return (
		<DiagnosisAuditReviewRowRoot>
			<L1DiagnosisReviewRowSource model={model} />
			{status}
			<L1DiagnosisReviewRowEvidence
				model={model}
				onDxEvidenceClick={handleDxEvidenceClick}
				onRxEvidenceClick={handleRxEvidenceClick}
			/>
			<DiagnosisWrapper>{model.title}</DiagnosisWrapper>
			<IcdWrapper>
				<IcdCode>{model.icd10Code}</IcdCode>
			</IcdWrapper>
			<ComorbidWrapper>{model.icdComorbidityStatus}</ComorbidWrapper>
			<ActionsWrapper>
				{diagnosisReviewItemCanTrash(model) ? (
					<L1PlainIconButton onClick={onTrashClick} icon={faTrash} color={FusionColor.BlueMain} />
				) : null}
			</ActionsWrapper>
			<AuditDecisionWrapper>
				{
					diagnosisReviewItemRequiresAuditDecision(model) ? (
						<>
							<V2L1BooleanButton
								icon={faCheck}
								checkedColor={FusionColor.GoGreen}
								checked={model.auditDecision === AuditDecision.Accepted}
								onChange={(e) => handleDecisionChange(e.target.checked ? AuditDecision.Accepted : null)}
							/>
							<V2L1BooleanButton
								icon={faXmark}
								checkedColor={FusionColor.StopRed}
								checked={model.auditDecision === AuditDecision.Rejected}
								onChange={(e) => handleDecisionChange(e.target.checked ? AuditDecision.Rejected : null)}
							/>
						</>
					) : (
						<>
							<div></div>
							<div></div>
						</>
					)
				}
				<V2L1BooleanButton
					icon={faQuestion}
					checkedColor={FusionColor.OrangeMain}
					checked={model.auditDecision === AuditDecision.Question}
					onChange={(e) => handleDecisionChange(e.target.checked ? AuditDecision.Question : null)}
				/>
				<L1DiagnosisReviewAuditCommentButton
					model={model}
					onClick={onAuditCommentClick}
					requiresAction={diagnosisReviewItemIsPendingAuditComment(model, encounterAppLastStatusDT)}
				/>
			</AuditDecisionWrapper>
		</DiagnosisAuditReviewRowRoot>
	)
}

const EmptyHeader = styled.div({
	padding: '0 !important',
})

const DecisioningSubheader = styled.div(({theme}) => ({
	backgroundColor: theme.colors.third,
	borderTopRightRadius: theme.borderRadius,
	borderTopLeftRadius: theme.borderRadius,
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	alignItems: 'center',
	justifyItems: 'center',
}))

interface L3DiagnosisAuditReviewProps {
	models: IndexedModel<DiagnosisReviewItem>[]
	onChange: (modelIdx: number, updatedModel: DiagnosisReviewItem) => void
	onAuditCommentClick: (modelIdx: number) => void
	onAddDiagnosisClick: () => void
	onTrashClick: (icd: string) => void
}

const L3DiagnosisAuditReview = ({models, onChange, onAuditCommentClick, onAddDiagnosisClick, onTrashClick}: L3DiagnosisAuditReviewProps) => {
	const handleModelChange = (modelIdx: number) => (updatedModel: DiagnosisReviewItem) => {	
		onChange(modelIdx, updatedModel)
	}

	const handleAuditCommentClick = (modelIdx: number) => () => {
		onAuditCommentClick(modelIdx)
	}

	const handleTrashClick = (icd: string) => () => {
		onTrashClick(icd)
	}

	const items = models.map(({model, stateIdx}) => (
		<DiagnosisAuditReviewRow
			key={stateIdx}
			model={model}
			onChange={handleModelChange(stateIdx)}
			onAuditCommentClick={handleAuditCommentClick(stateIdx)}
			onTrashClick={handleTrashClick(model.icd10Code)}
		/>
	))

	// constrain dan's column in case he rambles!
	const dansComorbidString = 'minmax(auto, 8rem)'
	const gridTemplate = `max-content max-content max-content 1fr max-content ${dansComorbidString} max-content max-content`

	return (
		<L2TabularListRoot columnCount={7} gridTemplateColumns={gridTemplate}>
			<L2TabularListHeader>
				<div>Source</div>
				<div>Diagnosis Status</div>
				<div>Evidence</div>
				<div>Diagnosis</div>
				<div>ICD</div>
				<div>Comorbid</div>
				<EmptyHeader>{/* Trash */}</EmptyHeader>
				<DecisioningSubheader>
					<div>Accept</div>
					<div>Reject</div>
					<div>Query</div>
					<div></div>
				</DecisioningSubheader>
			</L2TabularListHeader>
			<L2TabularListContent>
				{items}
			</L2TabularListContent>
			<L2TabularListActions>
				<L1ReasonableButton icon={faPlus} label="Add" onClick={onAddDiagnosisClick} wide={true} />
			</L2TabularListActions>
		</L2TabularListRoot>
	)
}

export default L3DiagnosisAuditReview
